import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import AddIcon from '@material-ui/icons/Add';
import { useDropzone } from 'react-dropzone';
import { SessionStepProps } from '../SessionFlowBase';
import useSession from '../../Util/useSession';
import SessionVideoR2UploadSlot, { accept } from './SessionVideoR2UploadSlot';

interface Props extends SessionStepProps {}

const maxCameras = 10;

const SessionVideosR2Upload: React.FunctionComponent<Props> = ({ sessionid }) => {
  const { session, refetch } = useSession(sessionid);
  const videos = session?.videos || [];
  const used = videos.map((video) => video.sortOrder || 0);
  const [added, setAdded] = useState<number[]>([]);
  const slots = Array.from(new Set([...used, ...added])).sort((x, y) => x - y);
  if (slots.length === 0) {
    slots.push(0);
  }

  const onUploaded = useCallback(() => {
    refetch();
  }, [refetch]);

  const [dropped, setDropped] = useState<Record<number, File>>({});

  const addCamera = (files: File[] = []) => {
    let next = Math.max(...slots) + 1;
    const slotsToAdd: number[] = [];
    const filesBySlot: Record<number, File> = {};
    (files.length ? files : [undefined]).forEach((file) => {
      if (slots.length + slotsToAdd.length >= maxCameras) {
        return;
      }
      slotsToAdd.push(next);
      if (file) {
        filesBySlot[next] = file;
      }
      next++;
    });
    setAdded([...added, ...slotsToAdd]);
    setDropped({ ...dropped, ...filesBySlot });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accept.split(','),
    noClick: true,
    onDrop: (files) => addCamera(files),
  });

  const removeCamera = (sortOrder: number) => setAdded(added.filter((x) => x !== sortOrder));

  return (
    <Container>
      {slots.length < maxCameras && (
        <AddCamera {...getRootProps({ onClick: () => addCamera() })} active={isDragActive}>
          <input {...getInputProps()} />
          <AddIcon fontSize="small" />
          <span>
            <strong>Add camera</strong> · or drop video files here, one camera each
          </span>
        </AddCamera>
      )}
      <Wrapper>
        {slots.map((sortOrder, index) => (
          <SessionVideoR2UploadSlot
            key={sortOrder}
            sessionId={Number(sessionid)}
            sortOrder={sortOrder}
            label={`Camera ${index + 1}`}
            video={videos.find((video) => video.sortOrder === sortOrder)}
            initialFile={dropped[sortOrder]}
            base={videos.find((video) => video.sortOrder !== sortOrder && video.fps)}
            onUploaded={onUploaded}
            onRemove={slots.length > 1 && !used.includes(sortOrder) ? () => removeCamera(sortOrder) : undefined}
            onDeleted={onUploaded}
          />
        ))}
      </Wrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  align-items: start;
  gap: 16px;
`;

const AddCamera = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 14px;
  color: ${({ active }) => (active ? '#1d4ed8' : '#64748b')};
  background: ${({ active }) => (active ? '#eff6ff' : 'transparent')};
  border: 1px dashed ${({ active }) => (active ? '#60a5fa' : '#cbd5e1')};
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  strong {
    color: #0f172a;
  }
  &:hover {
    border-color: #94a3b8;
  }
`;

export default SessionVideosR2Upload;
